import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Grid, Typography, useMediaQuery, Container, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import * as campaignActions from './../../../actions/campaignActions';

import * as talentAlertApi from './../../../api/talentAlertApi';

import SingleCandidate from './SingleCandidate';
import { ContentWrapper } from './../../../components/LayoutComponents/ContentWrapper';
import { PageTitle } from './../../../components/LayoutComponents/PageTitle';
import TalentAlertBackButton from './../../../components/LayoutComponents/TalentAlertBackButton';

import { SUBMITTED, HIDDEN, REJECTED } from './../../../constants/candidateStatusIds';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    overflow: 'hidden'
  },
  headerSection: {
    position: 'relative',
    width: '100%',
    minHeight: '55vh',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      minHeight: '160px'
    }
  },
  headerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 1
  },
  headerContent: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '1200px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  videoContainer: {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    paddingTop: '170px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90%',
      paddingTop: '40px'
    }
  },
  video: {
    width: '100%',
    height: 'auto',
    maxHeight: '400px',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[4]
  },
  introSection: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(6, 2),
    marginBottom: '30px',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 2)
    }
  },
  introContainer: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  introHeader: {
    fontSize: '2rem',
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
      marginBottom: theme.spacing(2)
    }
  },
  introText: {
    fontSize: '1.1rem',
    lineHeight: 1.6,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem'
    }
  },
  candidatesSection: {
    padding: theme.spacing(4, 2),
    maxWidth: '1200px',
    margin: '0 auto'
  }
}));

const TalentAlertContactDashboard = ({ loggedIn, campaignId, consultancyId, isPreview, cancelPreview }) => {

  const classes = useStyles();

  const dispatch = useDispatch();

  const history = useHistory();

  const [t] = useTranslation('newTranslations');

  const isMobile = useMediaQuery('(max-width:767px)');

  const contactCampaign = useSelector((state) => state.campaign);

  const [isLoading, setIsLoading] = useState(false);
  const [campaignTitle, setCampaignTitle] = useState('');

  const campaignCandidates = contactCampaign.campaignCandidates;

  useEffect(() => {
    if (!loggedIn) {
      history.push(`/talent-alert/${campaignId}`);
      return;
    }

    if (isPreview) {
      setIsLoading(false);
      getContactCampaignById();
      return;
    }

    talentAlertApi.checkIfInvited(campaignId)
      .then((response) => {
        if (!response.data) {
          history.push(`/talent-alert/${campaignId}`);
          return;
        }

        setIsLoading(false);
        getContactCampaignById();
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (contactCampaign.campaignTitle) {
      setCampaignTitle(contactCampaign.campaignTitle);
    }
  }, [contactCampaign]);

  const getContactCampaignById = async () => {
    try {
      if (!isPreview) {
        const acceptedTCs = await talentAlertApi.checkIfAcceptedTCs(campaignId);
  
        if (!acceptedTCs.data.acceptedTCs) {
          history.push(`/terms-and-conditions/${campaignId}`);
          return;
        }
      }

      const talentAlert = await talentAlertApi.getContactTalentAlertById(campaignId, consultancyId);

      dispatch({
        type: campaignActions.UPDATE_CAMPAIGN,
        campaign: talentAlert.data
      });

      setIsLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <ContentWrapper loading={isLoading} removeGutters>
      <TalentAlertBackButton />

      {isPreview && (
        <PageTitle
          pageTitle={ `${campaignTitle} - ${t("newTranslations:recruiterPreviewTalentAlertAsContactPreviewHeaderAdditionalLabel")}` }
          btnLabel={ t("newTranslations:recruiterPreviewTalentAlertAsContactBtnCancelPreview") }
          btnFunc={ cancelPreview }
          btnVariant="outlined" />
      )}

      <div className={classes.root}>
        {/* Header Section with Background Image */}
        <Box 
          className={classes.headerSection}
          style={{ backgroundImage: `url(${contactCampaign?.headerImg})` }}
        >
          <div className={classes.headerOverlay} />
          <Container className={classes.headerContent}>
            {contactCampaign?.campaignVideo && (
              <div className={classes.videoContainer}>
                <video 
                  className={classes.video}
                  poster={isMobile ? contactCampaign?.headerImg : ""} 
                  src={contactCampaign.campaignVideo} 
                  controls 
                  allowFullScreen
                  preload="metadata"
                />
              </div>
            )}
          </Container>
        </Box>

        {/* Introduction Section */}
        <Box className={classes.introSection}>
          <Container className={classes.introContainer}>
            <Grid container justifyContent="center" alignItems="center">
              <Grid item xs={12} md={8} lg={6} style={{ margin: '0 auto' }}>
                <Typography className={classes.introHeader} align="center">
                  {contactCampaign.introduction?.greetingCaption}
                </Typography>
                
                <Typography className={classes.introText} align="center">
                  {contactCampaign.introduction?.introductionText}
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>
        
        {/* Candidates Section */}
        <Box className={classes.candidatesSection}>
          {campaignCandidates.map((candidate) => {
            // if candidate status is anything but visible do not show
            if (candidate.status !== SUBMITTED && candidate.status !== HIDDEN && candidate.status !== REJECTED) {
              return (
                <SingleCandidate
                  candidateData={candidate}
                  campaignId={campaignId}
                  consultancyId={consultancyId}
                  key={candidate?.id}
                  loggedIn={loggedIn} />
              );
            }

            return null;
          })}
        </Box>
      </div>
    </ContentWrapper>
  );

};

export default TalentAlertContactDashboard;
